import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form3800 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 3800: General Business Credits | TaxZerone"
          description="Learn how to file IRS Form 3800 with Form 990-T to claim business credits. Get insights on eligibility, filing steps, and maximizing your tax benefits."
          keywords=""
          canonical="resources/tax-exempt/form-3800-business-credits/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 3800</span>
          </span>{' '}
          General Business Credit
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f3800`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-3800-business-credits/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 3800 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 3800? Form 3800, General Business Credit,
                        </strong>{' '}
                        is used to{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          claim and calculate the general business credit
                        </strong>{' '}
                        and determine the amount of credit allowed against tax.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Taxpayers claiming one or more{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          general business credits
                        </strong>{' '}
                        generally must complete Form 3800 along with the
                        applicable{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          credit source forms
                        </strong>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 3800 reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          current-year business credits, credit carryforwards
                          and carrybacks, credit limitations, passive activity
                          credits,
                        </strong>
                        and the amount of credit allowed for the tax year.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:{' '}
                        </strong>{' '}
                        Form 3800 may be used with{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-T
                        </Link>
                        ,{' '}
                        <strong style={{ fontWeight: 600 }}> Form 1041</strong>
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1065-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 1065
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}> Form 1120</strong>,{' '}
                        <Link
                          href="https://www.taxzerone.com/business-tax/efile-irs-form-1120-s/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 1120-S
                        </Link>{' '}
                        and other applicable returns when the taxpayer is
                        claiming eligible general business credits.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Credit Source Forms:
                        </strong>{' '}
                        Individual credits are generally calculated on their
                        applicable source forms, such as{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-3468-investment-credit/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 3468
                        </Link>
                        ,{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          (Investment Credit), Form 5884 (Work Opportunity
                          Credit),{' '}
                        </strong>{' '}
                        or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 6765 (Credit for Increasing Research
                          Activities),{' '}
                        </strong>{' '}
                        , and then reported on Form 3800.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    // scrollMarginTop: 87,
                  }}
                >
                  What's New in Form 3800:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  New Item B:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Check Item B(i) to confirm if you made an entry in Part III,
                    column (f), as a transferor or transferee of credits under
                    section 6418.
                  </li>
                  <li>
                    If "Yes," enter the number of{' '}
                    <Link
                      href="/resources/tax-exempt/transfer-election-statement/"
                      sx={{ color: '#0202ff' }}
                    >
                      transfer election statements
                    </Link>{' '}
                    attached to your return in Item B(ii).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Schedule A (Form 3800):
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Use{' '}
                    <Link
                      href="/resources/tax-exempt/form-3800-schedule-a/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Schedule A (Form 3800)
                    </Link>{' '}
                    as your transfer election statement.
                  </li>
                  <li>
                    Report all required information for each transferred credit
                    on this schedule.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Small Agri-Biodiesel Producer Credit:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Figure your credit using{' '}
                    <Link
                      href="https://www.irs.gov/pub/irs-pdf/f8864.pdf"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 8864
                    </Link>
                    .
                  </li>
                  <li>
                    Report the credit on IRS Form 3800, Part III, line 1l.{' '}
                  </li>
                  <li>
                    For fuel sold or used{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      after June 30, 2025,
                    </span>{' '}
                    you can make the section 6418 transfer election.{' '}
                  </li>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 3800?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who should use Form 3800 with Form 990‑T?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whyis" sx={{ color: '#0202ff' }}>
                          Why is Form 3800 important for 990‑T filers?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howdoes" sx={{ color: '#0202ff' }}>
                          How does Form 3800 work in the 990‑T process?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#complete" sx={{ color: '#0202ff' }}>
                          Complete Form 3800 with Detailed Line-by-Line
                          Instructions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What is Form 3800?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 3800 is the IRS form used to report the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    “General Business Credit,”
                  </span>{' '}
                  which is a collection of many different business-related tax
                  credits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Instead of handling each credit in isolation, Form 3800 pulls
                  them together, applies IRS limitation rules, and shows how
                  much credit can actually be used this year against your tax or
                  UBIT.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who should use Form 3800 with Form 990‑T?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 3800 is relevant to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    tax-exempt organizations that
                  </span>{' '}
                  file Form{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    990‑T
                  </Link>{' '}
                  and:
                </Typography>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 16 }}>
                    Have unrelated business income that is subject to UBIT, and
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Generate one or more general business credits (for example,
                    through qualifying employment, energy, investment, or
                    similar activities).
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization is filing Form 990-T and claiming any
                  eligible general business credit, you generally need Form 3800
                  to compute and apply those credits correctly.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whyis"
                >
                  Why is Form 3800 important for 990‑T filers?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For 990‑T filers, Form 3800 can:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Help reduce the UBIT liability with allowable credits</li>
                  <li>
                    Prevent loss of credits by correctly tracking carrybacks and
                    carryforwards{' '}
                  </li>
                  <li>
                    Provide a clear, organized summary of all general business
                    credits affecting your 990‑T
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In other words, it helps ensure that your organization pays
                  the correct amount of UBIT-no more, no less-while taking full
                  advantage of the credits you are entitled to.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="howdoes"
                >
                  How does Form 3800 work in the 990‑T process?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  At a high level, a 990‑T filer will:
                </Typography>
                <Box component="ol" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Identify unrelated business activities and compute UBIT on
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990‑T
                    </Link>
                    .{' '}
                  </li>
                  <li>
                    Determine which activities generate general business credits
                    and complete the related credit forms/schedules.{' '}
                  </li>
                  <li>
                    Use Form 3800 to combine those credits, apply limitations,
                    and compute the total allowable credit for the year.{' '}
                  </li>
                  <li>
                    Apply the allowable credit from Form 3800 to reduce the UBIT
                    reported on Form 990‑T, and track any unused credit for
                    other years.{' '}
                  </li>
                </Box>

                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Embrace the Future of Tax Filing – Choose TaxZerone
                        Today!
                      </Typography>
                      <Typography variant="body2">
                        Make your e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form3800`}
                      >
                        Start E-filing Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="complete"
                >
                  Complete Form 3800 with Detailed Line-by-Line Instructions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-general-business-img.webp" />
                  <source srcSet="/assets/images/f3800-general-business-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-general-business-img.png"
                    loading="lazy"
                    alt=" IRS Form 3800 General Business Credit with A and B questions"
                    width={760}
                    height={197}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Name(s) Shown on Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    name of the taxpayer or business
                  </span>{' '}
                  exactly as it appears on the tax return.This should match the
                  name used on all supporting forms and schedules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Identifying Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the taxpayer's{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    identification number,
                  </span>{' '}
                  such as:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Social Security Number (SSN)
                    </span>{' '}
                    for individuals,
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Employer Identification Number (EIN){' '}
                    </span>{' '}
                    for businesses and organizations, or{' '}
                  </li>
                  <li>Other applicable taxpayer identification number. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This number must match the identification number shown on the
                  tax return.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line A – Corporate Alternative Minimum Tax (CAMT) and BEAT
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Check <span style={{ fontWeight: 600 }}>"Yes"</span> if you
                  are both
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    An{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      applicable corporation
                    </span>{' '}
                    subject to the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Corporate Alternative Minimum Tax (CAMT)
                    </span>
                    under section 59(k), and
                  </li>
                  <li>
                    An{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      applicable taxpayer
                    </span>{' '}
                    subject to the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Base Erosion Anti-Abuse Tax (BEAT)
                    </span>{' '}
                    under section 59A(e).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Otherwise, check{' '}
                  <span style={{ fontWeight: 600 }}>"No."</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line B(i) – Transfer Election Statement
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check <span style={{ fontWeight: 600 }}> "Yes"</span> if you
                  entered an amount in{' '}
                  <span style={{ fontWeight: 600 }}> Part III, column (f)</span>{' '}
                  for a credit transfer election. Otherwise, check{' '}
                  <span style={{ fontWeight: 600 }}> "No."</span>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line B(ii) – Number of Transfer Election Statements
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you checked{' '}
                  <span style={{ fontWeight: 600 }}> "Yes" </span> on Line B(i),
                  enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    number of transfer election statements
                  </span>{' '}
                  attached to your return.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I -Credits Not Allowed Against Tentative Minimum Tax
                  (TMT)
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f3800-part1-img.webp" />
                  <source srcSet="/assets/images/f3800-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part1-img.png"
                    loading="lazy"
                    alt="Form 3800 part-i : Credits Not Allowed Against Tentative Minimum Tax (TMT) form line 1 to line 6"
                    width={760}
                    height={177}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Part I is used to{' '}
                  <span style={{ fontWeight: 600 }}>
                    separate credits that are NOT allowed against Tentative
                    Minimum Tax (TMT)
                  </span>{' '}
                  and track:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Non‑passive credits from Part III</li>
                  <li>Passive activity credits and their allowed amount </li>
                  <li>Non‑passive carryforwards and carrybacks </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Users should complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Parts III and IV first,
                  </span>{' '}
                  then fill in Part I using the references shown on each line.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Go to Part III, line 2.</li>
                  <li>Take the full amount from column (e). </li>
                  <li>
                    Add these amounts together and enter the total on Part I,
                    line 1.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong> From
                  column (f), add only the non‑passive part and ignore any
                  passive‑activity amounts.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>In Part III, line 2, find: </li>
                  <li>The amount in column (d) (passive credits), and </li>
                  <li>The passive portion of column (f). </li>
                  <li>
                    In Part IV, line 6, take the amount in column (d) (passive
                    carryovers).{' '}
                  </li>
                  <li>Add all these passive amounts together. </li>
                  <li>Enter the total on Part I, line 2. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Use Form 8582‑CR or Form 8810 (whichever applies to the
                    filer) to calculate how much of the line 2 amount is allowed
                    for 2025.{' '}
                  </li>
                  <li>Enter that allowed passive credit on Part I, line 3. </li>
                  <li>If no passive credit is allowed, enter 0. </li>
                  <li>Do not include any specified credits here. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Go to Part IV, line 6, column (f). </li>
                  <li>
                    From that amount, identify the non‑passive carryforwards
                    (credits from activities that are not passive).{' '}
                  </li>
                  <li>
                    Enter that non‑passive carryforward amount on Part I, line
                    4.{' '}
                  </li>
                  <li>
                    If the carryforward amount you are entering is different
                    from what you originally reported in a prior year, check the
                    box on line 4 and follow the instructions for attaching the
                    required statement.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete line 5 only if you are amending your 2025 return to
                  carry back unused credits from 2026.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If the
                  filer is an{' '}
                  <span style={{ fontWeight: 600 }}> individual </span> claiming
                  the research credit from a sole proprietorship or
                  pass‑through:
                  <ul>
                    <li>
                      First, include the carryback amount when you compute the
                      research credit limitation on{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Part III, line 1c
                      </span>
                      .{' '}
                    </li>
                    <li>
                      Then, only the{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        allowed carryback portion
                      </span>{' '}
                      is brought here and entered on{' '}
                      <span style={{ fontWeight: 600 }}>line 5</span>.{' '}
                    </li>
                  </ul>
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Add the amounts on lines 1, 3, 4, and 5. </li>
                  <li>Enter the total on Part I, line 6. </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part II - Figuring Credit Allowed After Limitations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Section A-Figuring Credit Allowed After Section 38(c)(1)
                  Limitation Based on Amount of Tax
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part2-seca-img.webp" />
                  <source srcSet="/assets/images/f3800-part2-seca-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part2-seca-img.png"
                    loading="lazy"
                    alt="Form 3800 part-ii : Section A-Figuring Credit Allowed After Section 38(c)(1) Limitation Based on Amount of Tax form line 7 to line 17"
                    width={760}
                    height={556}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  What to enter on line 7 depends on the type of filer:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Individuals
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For individual filers, line 7 shows your regular tax before
                  credits. Add the amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 1040 / 1040‑SR / 1040‑NR,
                  </span>{' '}
                  line 16 to the amount from Schedule 2 (Form 1040), line 1z,
                  and enter that total on Form 3800, Part II, line 7. Exclude
                  any education credit recapture, any tax from Form 8621, and
                  any section 965(i) deferred tax or triggering‑event tax from
                  this calculation.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Corporations
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Go to Form 1120, Schedule J, line 2. </li>
                  <li>
                    Ignore (exclude) any amounts that were entered on lines 1c,
                    1e, and 1f of Schedule J.{' '}
                  </li>
                  <li>
                    Enter the remaining amount on Form 3800, line 7 (or use the
                    equivalent line on another corporate return if not filing
                    Form 1120).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Estates and trusts
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Go to Form 1041, Schedule G, lines 1a, 1b, and 1d. </li>
                  <li>Add these amounts together. </li>
                  <li>
                    If any Form 8978 amount is included on line 1e of Schedule
                    G, add that too.{' '}
                  </li>
                  <li>
                    Enter the total on Form 3800, line 7 (or use the equivalent
                    line if using a different fiduciary return).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Non‑pass‑through partners
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Partners that are not pass‑through entities (such as
                    partnerships or S corporations) use Form 8978 and Schedule A
                    (Form 8978) to compute their tax net of credits, not Form
                    3800, for that reporting year.{' '}
                  </li>
                  <li>
                    Any credit carryforwards that result from that Form 8978
                    calculation are then reported in the next tax year on Form
                    3800, Parts IV and VI.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8 }}>
                  <span style={{ fontWeight: 600 }}> Individuals:</span> Enter
                  the amount from Form 6251, line 11 on line 8.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8 }}>
                  <span style={{ fontWeight: 600 }}> Corporations: </span> Enter
                  the amount from Form 4626, Part II, line 13.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Estates and trusts: </span>{' '}
                  Enter the amount from Schedule I (Form 1041), line 54.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Add the amounts from line 7 (regular tax before credits) and
                    line 8 (AMT).{' '}
                  </li>
                  <li>Enter the total on line 9. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your foreign tax credit on line 10a, using the amount
                  from your main tax return or foreign tax credit schedule. This
                  credit is counted before figuring the general business credit
                  limit.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter on line 10b the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    other non–general‑business credits
                  </span>
                  listed for your filer type in the official instructions. These
                  credits are also taken into account before applying the
                  general business credit limitation.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10c
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Add line 10a (foreign tax credit) and line 10b (other
                    allowable credits from your main return).{' '}
                  </li>
                  <li>Enter the total on line 10c. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract <span style={{ fontWeight: 600 }}> line 10c</span>{' '}
                    from <span style={{ fontWeight: 600 }}> line 9</span>.
                  </li>
                  <li>
                    Enter the result on{' '}
                    <span style={{ fontWeight: 600 }}> line 11</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  If the result is{' '}
                  <span style={{ fontWeight: 600 }}> zero or less</span>:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}> 0</span> on line
                    11.{' '}
                  </li>
                  <li>
                    Skip{' '}
                    <span style={{ fontWeight: 600 }}>lines 12 through 15</span>
                    .{' '}
                  </li>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}> 0</span> on{' '}
                    <span style={{ fontWeight: 600 }}> line 16</span>.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Subtract line 10c from line 7. </li>
                  <li>Enter the result on line 12. </li>
                  <li>If the result is zero or less, enter 0. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13
                </Typography>

                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Look at line 11 (for individuals, estates, trusts) or line
                    12 (for corporations; see form instructions).{' '}
                  </li>
                  <li>
                    If that number is more than $25,000, subtract $25,000 and
                    multiply the excess by 25% (0.25).
                  </li>
                  <li>Enter this 25% amount on line 13.</li>
                  <li>If the applicable line is $25,000 or less, enter 0. </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8 }}>
                  <span style={{ fontWeight: 600 }}> Individuals:</span> Enter
                  the amount from Form 6251, line 9 on line 14.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8 }}>
                  <span style={{ fontWeight: 600 }}> Corporations: </span> Enter
                  0 (corporate AMT is generally repealed; follow current IRS
                  instructions).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Estates and trusts: </span>{' '}
                  Enter the amount from Schedule I (Form 1041), line 52.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Compare line 13 and line 14. </li>
                  <li>Enter the larger of the two amounts on line 15. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 16
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Subtract line 15 from line 11.</li>
                  <li>
                    If the result is greater than zero, enter the result on line
                    16.
                  </li>
                  <li>If the result is zero or less, enter 0. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 17 – Special rule for C corporations
                </Typography>
                <Typography variant="body1">
                  Line 17 applies <span style={{ fontWeight: 600 }}> only</span>{' '}
                  if a C corporation is limited by the ownership‑change rules.
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the corporation had a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      post‑1986 ownership change
                    </span>{' '}
                    (section 382), section{' '}
                    <span style={{ fontWeight: 600 }}>383</span> may limit how
                    much tax can be offset by{' '}
                    <span style={{ fontWeight: 600 }}>pre‑change</span> general
                    business credits.
                  </li>
                  <li>
                    If the corporation{' '}
                    <span style={{ fontWeight: 600 }}> acquired control</span>{' '}
                    of another corporation, or its assets in a{' '}
                    <span style={{ fontWeight: 600 }}> reorganization,</span>{' '}
                    section <span style={{ fontWeight: 600 }}>384</span> may
                    limit how much tax on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      recognized built‑in gains
                    </span>{' '}
                    can be offset by{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      pre‑acquisition
                    </span>{' '}
                    general business credits.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  If either applies:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Compute the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      maximum general business credit allowed
                    </span>{' '}
                    under section 383 and/or 384.
                  </li>
                  <li>
                    Enter that amount on{' '}
                    <span style={{ fontWeight: 600 }}>line 17</span> .{' '}
                  </li>
                  <li>
                    Attach your calculation and write{' '}
                    <span style={{ fontWeight: 600 }}> “Sec. 383” </span>or
                    <span style={{ fontWeight: 600 }}> “Sec. 384”</span> (as
                    appropriate) in the margin next to line 17.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Section B-Figuring Section 38(c)(2) Empowerment Zone and
                  Renewal Community Employment Credit Allowed
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part2-secb-img.webp" />
                  <source srcSet="/assets/images/f3800-part2-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part2-secb-img.png"
                    loading="lazy"
                    alt="Form 3800 part-ii section-b : Figuring Section 38(c)(2) Empowerment Zone and Renewal Community Employment Credit Allowed after tax liability limitations from line 18 to line 26"
                    width={760}
                    height={299}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on line 14 by 75% (0.75) and enter the
                  result on line 18.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 19
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Compare line 13 and line 18. Enter the larger of the two
                  amounts on line 19.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 19 from line 11. If the result is zero or less,
                  enter 0 on line 20.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 21
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract line 17 from line 20. If the result is zero or less,
                  enter 0 on line 21.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 22
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  On line 22, enter the non‑passive portion of the empowerment
                  zone and renewal community employment credit:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Take the amount from Part III, line 3, column (e).</li>
                  <li>
                    Add the non‑passive credit amounts from Part IV, line 3,
                    column (f).{' '}
                  </li>
                  <li>
                    Any carryforward or carryback of this credit (from the
                    relevant schedule) flows into this line.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 22
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    On line 23, enter the passive activity credit for this same
                    credit:
                  </li>
                  <li>Take the amount from Part III, line 3, column (d).</li>
                  <li>
                    Add the passive credit amounts from Part IV, line 3, column
                    (d).{' '}
                  </li>
                  <li>
                    This total represents the part of the credit that is subject
                    to passive activity limits.
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 24
                </Typography>
                <Typography variant="body1">
                  Use Form 8582‑CR or Form 8810 to figure how much of the
                  passive credit in line 23 is actually allowed this year.
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter that allowed amount on line 24. </li>
                  <li>If no passive credit is allowed, enter 0. </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note: </strong> Line 24
                  applies only to the empowerment zone and renewal community
                  employment credit on Part III, line 3, plus any prior‑year
                  passive amounts for the same credit.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 25
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>Add line 22 and line 24. </li>
                  <li>Enter the total on line 25.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have amounts on both line 23 and line 24, this total
                  includes both non‑passive and passive credit components. In
                  that case, attach a brief statement that shows how much of
                  line 25 is non‑passive and how much is passive.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution: </strong> Passive
                  activity credit amounts can generally only be used to offset
                  positive passive activity income, so keep them separate and
                  documented in your records.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 26
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the allowable Empowerment Zone and Renewal Community
                  Employment Credit, limited to the lesser of line 21 or line
                  25.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Section C-Figuring the Specified Credit Amount Allowed Under
                  Section 38(c)(4)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part2-secc-img.webp" />
                  <source srcSet="/assets/images/f3800-part2-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part2-secc-img.png"
                    loading="lazy"
                    alt="IRS Form 3800 Section C showing calculations for the specified credit amount allowed under Section 38(c)(4) from line 27 -line 37"
                    width={760}
                    height={368}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 31 – General Business Credit
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    general business credit amount from Part III, line 5
                  </span>
                  . Add the amount in{' '}
                  <span style={{ fontWeight: 600 }}> column (e)</span> to any
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    non-passive credit amounts in column (f)
                  </span>{' '}
                  . Do not include passive activity credit amounts from column
                  (f). Refer to the caution provided under Line 1 for additional
                  guidance.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 32 – Passive Activity Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total passive activity credits from Part III, line 5
                  </span>
                  . Combine the amount in column (d) with any{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    passive credit amounts in column (f)
                  </span>
                  . Exclude non-passive credit amounts reported in column (f).
                  Also include any passive specified credit carryovers from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part IV, column (d), line 5,
                  </span>{' '}
                  if applicable.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 33 – Passive Activity Credits Allowed Against TMT
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the current-year and carryover passive activity credit
                  amount allowed against Tentative Minimum Tax (TMT) as reported
                  on Form 8582-CR or Form 8810. Include only the general
                  business credits reported on Line 32. If no credits are
                  allowed, enter 0.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 34 – Credit Carryforwards
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total unused credit carryforwards available for 2025
                  from Part IV, line 5, column (f).
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, mb: 0.5, fontStyle: 'italic' }}
                >
                  Check the Box
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any credit amount was revised or adjusted from the amount
                  originally reported, check the box on this line and attach the
                  required statement.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, mb: 0.5, fontStyle: 'italic' }}
                >
                  Research Credit Note
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are claiming the research credit through a sole
                  proprietorship or pass-through entity, calculate the research
                  credit limitation first. Then enter the allowable carry
                  forward amount on Line 34.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, mb: 0.5, fontStyle: 'italic' }}
                >
                  Payroll Tax Credit Adjustment
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you elected to claim part of your research credit as a
                  payroll tax credit on Form 6765, reduce your research credit
                  carry forward by the amount elected.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 35 – Credit Carrybacks
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the carryback amount from Part IV, line 5, column (f).
                  Use this line only when amending your 2025 return to carry
                  back unused credits from 2026.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, mb: 0.5, fontStyle: 'italic' }}
                >
                  Research Credit Note
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are claiming the research credit through a sole
                  proprietorship or pass-through entity, apply the research
                  credit limitation first. Then enter the allowable carryback
                  amount on Line 35.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong>{' '}
                  Specified credits, including ESB credits, generally cannot be
                  carried back to tax years before they were first allowed
                  against TMT unless specifically permitted by law.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 36 – Total Available Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    lines 30, 33, 34, and 35
                  </span>
                  . Enter the total on line 36.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 37 – Allowed Specified Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    smaller line 29 or line 36
                  </span>
                  . This is the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    allowable specified credit amount.
                  </span>
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Section D-Credits Allowed After Limitations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part2-secd-img.webp" />
                  <source srcSet="/assets/images/f3800-part2-secd-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part2-secd-img.png"
                    loading="lazy"
                    alt="IRS Form 3800 Section D showing the calculation of credits allowed after limitation and the current-year credit allowed."
                    width={760}
                    height={122}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 38 – Unused Credit Amount
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Enter the amount on Line 38 as instructed.</li>
                  <li>
                    If the total of{' '}
                    <span style={{ fontWeight: 600 }}>Part II, line 38</span>{' '}
                    and{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part III, line 6, column (j)
                    </span>{' '}
                    is less than the combined total of Part I, lines 1, 3, and 4
                    and Part II, lines 30, 33, and 34 (excluding carrybacks),
                    you may have unused credits available for carryback or
                    carryforward. Refer to the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Carryback and Carryforward of Unused Credit
                    </span>{' '}
                    instructions for additional guidance.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part III -Current Year General Business Credits (GBCs)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part3-current-img.webp" />
                  <source srcSet="/assets/images/f3800-part3-current-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part3-current-img.png"
                    loading="lazy"
                    alt="IRS Form 3800 Part III showing current-year General Business Credits (GBCs) and related reporting fields from line 1a - line 2."
                    width={760}
                    height={537}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1 – Current-Year General Business Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Lines <span style={{ fontWeight: 600 }}>1a through 1zz</span>{' '}
                  list the{' '}
                  <span style={{ fontWeight: 600 }}>
                    current-year General Business Credits (GBCs)
                  </span>
                  available for the 2025 tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Each lettered line ({' '}
                  <span style={{ fontWeight: 600 }}>1a, 1b, 1c, ... , 1zz</span>
                  ) corresponds to a specific credit form shown in the left
                  column of Part III (for example,{' '}
                  <span style={{ fontWeight: 600 }}>
                    Form 3468, Form 7207, Form 6765, Form 8911, Form 8936,
                  </span>{' '}
                  and others).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For each applicable credit, complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    columns (a) through (j)
                  </span>
                  to report the credit amount, any transfer election or Elective
                  Payment Election (EPE) information, applicable limitations,
                  and the portion of the credit applied against your tax
                  liability.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}> lines 1a through 1zz</span>{' '}
                  and enter the total on{' '}
                  <span style={{ fontWeight: 600 }}> line 2.</span> This
                  represents your
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total current-year general business credits{' '}
                  </span>{' '}
                  before any applicable limitations or adjustments.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a) – Number of Items
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the number of facilities or pass-through entities listed
                  for the credit in Part V. If only one applies, you can leave
                  this column blank.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b) – Registration Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the registration number if you are making an{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Elective Payment Election (EPE)
                  </span>{' '}
                  or transfer election. If no election is being made, leave this
                  column blank.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c) – Pass-Through or Transfer Credit Entity EIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the EIN of the entity that provided the credit, such as
                  a pass-through entity, cooperative, or transferor. If multiple
                  entities are involved, enter the EIN of the entity associated
                  with the largest credit amount.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d) – Credits Subject to Passive Activity Limits
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter current-year credits that are subject to passive
                  activity limitations before applying any limitations.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e) – Credits Not Subject to Passive Activity Limits
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter credits that are not subject to passive activity
                  limitations due to your material participation in the business
                  activity.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f) – Credit Transfer Election Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter transferred credits as follows:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Negative amount</span> if
                    you transferred the credit to another taxpayer.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Positive amount</span> if
                    you received the credit as a transferee.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h) – Gross EPE Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total credit amount eligible for an Elective Payment
                  Election before applying any limitations or reductions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i) – Credit Applied Against Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the portion of the credit used to reduce your tax
                  liability. Apply carryforward credits before current-year
                  credits.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (j) – Net EPE Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the smaller of:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The amount in{' '}
                    <span style={{ fontWeight: 600 }}> column (h)</span>,
                    or{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Column (g) minus column (i).
                    </span>{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If you have
                  no tax liability, enter the amount from column (h) as the net
                  EPE amount. If you are making EPEs for multiple credits,
                  calculate the net EPE amount according to the IRS credit
                  ordering rules.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f3800-part3-line3-img.webp" />
                  <source srcSet="/assets/images/f3800-part3-line3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part3-line3-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 3800) Transfer Election Statement showing fields for transferor and transferee names, TINs, and addresses."
                    width={760}
                    height={326}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the current-year credit amount from Form 8844 in the
                  applicable columns. Complete columns (a) through (j) as
                  required.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Lines 4a through 4z list the current-year specified credits
                  eligible for reporting in Part III. Enter each credit on its
                  corresponding line and complete the applicable columns (a)
                  through (j).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 4a through 4z and enter the total
                  on line 5. This represents your total current-year specified
                  credits.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts from lines 2, 3, and 5 and enter the total on
                  line 6. This is your total current-year general business
                  credit amount before applying any limitations or adjustments.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Columns (a)–(j)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Complete columns (a) through (j) for lines 1a–1zz to report
                  current-year general business credits. These columns are used
                  to provide details such as the number of items, registration
                  numbers, entity EINs, passive and non-passive credit amounts,
                  transferred credits, Elective Payment Election (EPE) amounts,
                  credits applied against tax, and net EPE amounts. Complete
                  only the columns that apply to the credit being reported.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                  id="part4"
                >
                  Part IV- Carryovers of General Business Credits (GBCs)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part4-carryovers-img.webp" />
                  <source srcSet="/assets/images/f3800-part4-carryovers-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part4-carryovers-img.png"
                    loading="lazy"
                    alt="IRS Form 3800 Part IV Carryovers of General Business Credits (GBCs) fields from line 1a - line 1 zz."
                    width={760}
                    height={536}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1 – General rule
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Lines 1a through 1zz list the current‑year carryovers of
                    specific general business credits into 2025.
                  </li>
                  <li>
                    Each lettered line (1a, 1b, 1c, …, 1zz) corresponds to a
                    particular credit form shown in the left column of Part IV
                    (for example,{' '}
                    <Link
                      href="/resources/tax-exempt/form-3468-investment-credit/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 3468
                    </Link>{' '}
                    ,{' '}
                    <Link
                      href="/resources/tax-exempt/form-8911-alternative-fuel-credits/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8911
                    </Link>
                    ,{' '}
                    <Link
                      href="/resources/tax-exempt/form-8936-clean-vehicle-credit/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8936
                    </Link>
                    , etc.).
                  </li>
                  <li>
                    On each line, complete columns (a) through (i) to describe
                    the carryover for that specific credit.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a) – Number of items
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you have more than one source credit form for the same
                    credit (for example, multiple Forms 5884),{' '}
                  </li>
                  <li>
                    Show the detailed breakdown in Part VI, and in Part IV,
                    column (a), enter the number of source forms.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If you
                  have 3 Forms 5884 feeding into that line, enter “3” in column
                  (a).
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b) – Originating tax year
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the tax year where the carryover started (the year the
                    unused credit was first created).{' '}
                  </li>
                  <li>
                    This could be a prior year, or (for amended returns) a later
                    year that now carries back into 2025.{' '}
                  </li>
                  <li>
                    If you have carryovers from several years for the same
                    credit, enter the most recent year in column (b).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c) – Pass‑through entity EIN
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the credit was allocated by a pass‑through entity (like a
                    partnership or S‑corp), enter that entity’s EIN here.{' '}
                  </li>
                  <li>
                    If there are multiple pass‑through entities for the same
                    credit and they are broken down in Part VI, enter the EIN of
                    the entity that allocated the largest amount of that credit
                    to you.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d) – Carryovers subject to passive activity limits
                  (before limitations)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the full carryover amounts of credits that come from
                    passive activities (as defined in section 469).
                  </li>
                  <li>
                    This is the amount before applying the passive activity
                    limitations.{' '}
                  </li>
                  <li>
                    For transferred credits under section 6418, see any special
                    rules in the “Transferee taxpayer” guidance.{' '}
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e) – Carryovers subject to passive activity limits
                  (after limitations)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    This column shows the portion of the passive credit (from
                    column (d)) that is actually allowed after applying passive
                    activity rules.{' '}
                  </li>
                  <li>
                    Use Form 8582‑CR or Form 8810 (if required) to compute this
                    allowed amount.{' '}
                  </li>
                  <li>Enter the allowed passive carryover in column (e). </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f) – Carryovers not subject to passive activity limits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Column (f) lists non‑passive carryover credits.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g) – Amount of columns (e) and (f) applied against tax
                  in Part II
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the total portion of columns (e) and (f) that you
                    actually used to reduce tax in Part II of Form 3800.{' '}
                  </li>
                  <li>
                    For 2025, line 7 of column (g) should equal the sum of Part
                    II, lines 33, 34, and 35.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h) – Amount of columns (e) and (f) recaptured or
                  otherwise adjusted
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any amount from columns (e) and (f) that is recaptured
                    (for example, reported on Form 4255 or elsewhere).{' '}
                  </li>
                  <li>
                    Also include credits reduced or adjusted because of an audit
                    or other change.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i) – Carry forward to 2026
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    This is the remaining carryover after what you used and what
                    was recaptured.{' '}
                  </li>
                  <li style={{ fontWeight: 600 }}>
                    {' '}
                    Column (i)= (column (e)+ column (f)  ˆ’ (column (g) + column
                    (h)))
                  </li>
                  <li>
                    These amounts will appear as carryovers into 2026 and will
                    be included on your{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      2026 Form 3800, Part IV
                    </span>
                    .{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f3800-part4-line2-img.webp" />
                  <source srcSet="/assets/images/f3800-part4-line2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part4-line2-img.png"
                    loading="lazy"
                    alt="IRS Form 3800 Part IV Carryovers of General Business Credits (GBCs) fields from line 2a - line 3."
                    width={760}
                    height={438}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2 – General rule
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Lines 2a through 2zz</span>{' '}
                  list carry forwards of older general business credits that are
                  being brought into 2025, where the underlying credits are no
                  longer available as new, current‑year credits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Each lettered line (2a, 2b, 2c, …, 2zz) corresponds to a
                  specific “expired” credit form or provision shown in the left
                  column of Part IV (for example, Form 5884‑A, pre‑2008 Form
                  8586, pre‑2005 Form 6478, Form 8910, and other listed
                  credits).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a)- Column(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete these columns for lines 2a–2zz using the same rules
                  explained above for{' '}
                  <Link href="#part4" sx={{ color: '#0202ff' }}>
                    {' '}
                    Part IV, lines 1a–1zz
                  </Link>{' '}
                  number of items, originating tax year, pass‑through EIN,
                  passive and non‑passive carryovers, amounts used or
                  recaptured, and carryforward to 2026.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part4-line4-img.webp" />
                  <source srcSet="/assets/images/f3800-part4-line4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part4-line4-img.png"
                    loading="lazy"
                    alt="IRS Form 3800 Part IV Carryovers of General Business Credits (GBCs) fields from line 4 - line 7."
                    width={760}
                    height={336}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Lines(4a–4z)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Lines 4a–4z are used to report carryovers for a specific list
                  of <span style={{ fontWeight: 600 }}> “specified”</span>{' '}
                  general business credits that are carried into tax year 2025.
                  Each lettered line identifies one credit type.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5 – Add lines 4a–4z
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 5 provides the total carryover for all specified credits
                  listed on lines 4a–4z.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6 – Add lines 1a through 2zz
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 6 summarizes the carryovers from all earlier sections of
                  Part IV (lines 1 and 2).
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7 – Add lines 3, 5, and 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  On line 7, add the amounts from lines 3, 5, and 6 (by column
                  as the form indicates). Line 7 represents the final total of
                  all general business credit carryovers to tax year 2025 shown
                  in Part IV and is used to support the carryover figures in
                  Part II.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a)- Column(i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Complete these columns for lines 2a–2zz using the same rules
                  explained above for{' '}
                  <Link href="#part4" sx={{ color: '#0202ff' }}>
                    {' '}
                    Part IV, lines 1a–1zz
                  </Link>{' '}
                  number of items, originating tax year, pass‑through EIN,
                  passive and non‑passive carryovers, amounts used or
                  recaptured, and carry forward to 2026.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part V- Breakdown of Aggregate Amounts on Part III for
                  Facility-by-Facility, Multiple Pass-Through Entities, etc.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part5-img.webp" />
                  <source srcSet="/assets/images/f3800-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part5-img.png"
                    loading="lazy"
                    alt=" IRS Form 3800 Part V Breakdown of Aggregate Amounts on Part III for Facility-by-Facility, Multiple Pass-Through Entities, etc."
                    width={760}
                    height={505}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If every Part III amount comes from only one facility and one
                  entity, you can usually leave Part V blank. If any Part III
                  amount combines more than one facility, entity, or
                  registration number, you must use Part V to show the
                  breakdown.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a)- Column(i)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      exact line number from Part III
                    </span>{' '}
                    you are breaking down.
                  </li>
                  <li>
                    Include both the number and the letter{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      (for example, 1b, 2a, 3c)
                    </span>
                    .{' '}
                  </li>
                  <li>
                    This connects each Part V row to the correct line in Part
                    III.
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b) – Elective payment or transfer registration number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For each facility or credit that has its own{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    elective payment or transfer registration number,
                  </span>{' '}
                  enter that number in column (b) so the IRS can match this row
                  back to your registration.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c)(1) – Pass‑through entity EIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the credit was allocated to you from a pass‑through entity
                  (partnership, S corporation, or other pass‑through), enter
                  that entity’s EIN here.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c)(2) – Transferor entity EIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are involved in a transferable credit under section
                  6418(either as transferor or transferee), Enter the
                  transferor’s EIN here (the entity that originally earned the
                  credit)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)(1) – Credits other than credit transfer election
                  credits
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Put credits here that are not involved in a transfer under
                    section 6418.{' '}
                  </li>
                  <li>
                    These are your “normal” passive activity credits
                    (self‑earned or from pass‑throughs) that you did not sell or
                    buy as transfer credits.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)(2) – Credit transfer election credits sold
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Put in the amount of credits you sold under section 6418.
                  </li>
                  <li>
                    These are credits you transferred to someone else (you no
                    longer keep them).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)(3) – Credit transfer election credits purchased
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Put in the amount of credits you purchased under section
                    6418.{' '}
                  </li>
                  <li>
                    These are credits someone else earned but transferred to
                    you.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)(4) – Credits allowed after passive activity limit
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  This column shows the allowed credit amount after passive
                  activity limits. To get this amount:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Start with column (d)(1).</li>
                  <li>Subtract column (d)(2) (credits you sold). </li>
                  <li>Add column (d)(3) (credits you purchased). </li>
                  <li>
                    Then apply the passive activity rules (using Form 8582‑CR or
                    Form 8810, if required).{' '}
                  </li>
                  <li>
                    The final allowed amount after those limits goes in column
                    (d)(4).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e) – Credits other than transfer election credits
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Put non‑transfer credits here that are not passive.</li>
                  <li>
                    This includes credits you earned yourself or credits
                    allocated to you by a pass‑through entity.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f)(1) – Transfer election credits sold
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Put here the{' '}
                    <span style={{ fontWeight: 600 }}>
                      amount of credits you sold
                    </span>{' '}
                    under section 6418 that are{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      not subject to passive limits.
                    </span>
                  </li>
                  <li>
                    These are non‑passive credits that you transferred away.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f)(2) – Purchased transfer election credits not
                  subject to passive activity limit
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Put here the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      amount of non‑passive credits you purchased
                    </span>{' '}
                    under section 6418.{' '}
                  </li>
                  <li>
                    These are credits you acquired from someone else that are
                    not passive for you.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g) – Combine columns (d)(4), (e), (f)(1), and (f)(2)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    This column shows your{' '}
                    <span style={{ fontWeight: 600 }}> total credit</span> for
                    that line.{' '}
                  </li>
                  <li>To compute it:</li>
                  <ul>
                    <li>Add columns (d)(4), (e), and (f)(2). </li>
                    <li>Then subtract column (f)(1). </li>
                  </ul>
                  <li>
                    Think of column (g) as all credits for this
                    facility/registration after transfers and limits.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h)(1) – Gross EPE amount
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    This is the part of column (g) that is eligible for an
                    elective payment election (EPE) under section 6417 or
                    section 48D.{' '}
                  </li>
                  <li>Only put in credits that qualify for EPE. </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> credits
                  that you sold or credits you received under section 6418
                  cannot be used for section 6417 EPE. They do not go into
                  column (h)(1).
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  CColumn (h)(2) – Credit excluding EPE
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Here you enter column (g) minus column (h)(1). </li>
                  <li>
                    This is the portion of your total credit that is not part of
                    the gross EPE amount.{' '}
                  </li>
                  <li>
                    This remaining amount can include credits that are still
                    subject to the passive activity rules and may only offset
                    tax on your passive activities.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i)(1) – Amount of column (h)(2) applied against tax in
                  Part II
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Put here how much of the non‑EPE credit (from column (h)(2))
                    you actually used to reduce tax in Part II.{' '}
                  </li>
                  <li>
                    Remember: carryforward credits (from Part II, line 34) are
                    used first, before current‑year credits from Part III or
                    Part V.{' '}
                  </li>
                  <li>
                    So column (i)(1) represents the portion of column (h)(2)
                    that was applied after that ordering rule.{' '}
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i)(2) – Amount of EPE applicable credit in column
                  (h)(1) applied against tax in Part II
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Put here the amount of the EPE‑eligible credit (column
                    (h)(1)) that first gets applied to tax in Part II.{' '}
                  </li>
                  <li>
                    By rule, credits in column (h)(1) must be used to offset any
                    tax before the remaining amount can be treated as a payment
                    (elective payment/direct pay).{' '}
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (j) – Net EPE amount
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    This is your remaining elective payment amount after
                    applying some or all of it to tax.{' '}
                  </li>

                  <li>Column (j)=column (h)(1)  ˆ’column (i)(2) </li>
                  <li>
                    The result is the net EPE amount that can be treated as a
                    payment of tax (subject to the rules). Some or all of this
                    can effectively become a refund.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (k) – Carryforward to 2026
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>This is the credit you still carry forward to 2026. </li>
                  <li>Column (k): column (h)(2) - column (i)(1). </li>
                  <li>
                    These carryforwards will appear in your 2026 Form 3800, Part
                    IV, as part of your future‑year credit computations.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Part VI - Breakdown of Aggregate Amounts in Part IV :
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3800-part6-img.webp" />
                  <source srcSet="/assets/images/f3800-part6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3800-part6-img.png"
                    loading="lazy"
                    alt="IRS Form 3800 Part VI Breakdown of Aggregate Amounts in Part IV fields from line 1 to line 35."
                    width={760}
                    height={515}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Part VI only if a number you entered in Part IV is an
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    aggregate amount,
                  </span>{' '}
                  meaning it combines credits from more than one tax year or
                  more than one pass‑through entity. If your Part IV amounts
                  come from just a single year and a single entity, you
                  generally do not need to complete Part VI.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> If the
                  carry-forward amount on a Part IV line includes credits from
                  2023 and 2024, you must show one line in Part VI for 2023 and
                  a separate line for 2024.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a) – Line number from Part IV
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the Part IV line number that this breakdown relates
                    to.{' '}
                  </li>
                  <li>
                    This tells which Part IV total you are “unpacking”
                    here.{' '}
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (b) – Originating tax year
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the tax year when the credit was originally generated
                    (for example, 2023, 2024, etc.).{' '}
                  </li>
                  <li>
                    Each year gets its own line, even if it relates to the same
                    Part IV line.{' '}
                  </li>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c) – Pass-through entity EIN
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the credit comes from a partnership, Corporation, estate,
                    or trust, enter that entity’s EIN here.{' '}
                  </li>
                  <li>
                    If the credit is not from a pass-through entity, you can
                    leave this column blank.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Columns (d) and (e) – Subject to the passive activity limits
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d) – Before the passive activity limitations
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount of credit{' '}
                    <span style={{ fontWeight: 600 }}> before</span> applying
                    passive activity limitations.{' '}
                  </li>
                  <li>
                    Think of this as the{' '}
                    <span style={{ fontWeight: 600 }}> “raw credit”</span> for
                    that year/entity.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (e) – After the passive activity limitations
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the allowed credit amount after passive activity
                    limits are applied.{' '}
                  </li>
                  <li>
                    This is usually the credit that can move forward to be used
                    on IRS Form 3800.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the credit is <span style={{ fontWeight: 600 }}> not</span>{' '}
                  subject to passive activity limits, users typically leave
                  columns (d) and (e) blank and use column (f) instead.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (f) – Not subject to passive activity limits
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Use this column if the credit{' '}
                    <span style={{ fontWeight: 600 }}> is not</span> subject to
                    passive activity limitations.{' '}
                  </li>
                  <li>
                    Enter the carryover credit amount for that year/entity that
                    is{' '}
                    <span style={{ fontWeight: 600 }}> fully available </span>{' '}
                    (before applying current‑year tax limits in Form 3800).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (g) – Amount applied against tax in Part II
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the portion of columns (e) and/or (f) that is actually
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      used (applied)
                    </span>{' '}
                    against current‑year tax in{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part II of Form 3800.
                    </span>{' '}
                  </li>
                  <li>
                    This tells how much of that specific year’s credit has been
                    consumed.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (h) – Amount recaptured or otherwise adjusted
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any part of the credit that is{' '}
                    <span style={{ fontWeight: 600 }}> recaptured</span> or
                    <span style={{ fontWeight: 600 }}> adjusted</span> (for
                    example, due to a recapture event or other required
                    adjustment).
                  </li>
                  <li>
                    This column explains why some of the original carryover
                    disappears{' '}
                    <span style={{ fontWeight: 600 }}> without being used</span>{' '}
                    as a credit.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i) – Carry-forward to 2026
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    This is the{' '}
                    <span style={{ fontWeight: 600 }}> remaining credit</span>{' '}
                    that still carries forward to the next year.{' '}
                  </li>
                  <li>
                    To figure the amount in column (i), add the amounts in
                    columns (e) and (f), then subtract the amounts in columns
                    (g) and (h). The result is your carry forward to 2026.
                  </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is derived from IRS guidance.
                        For complete details on eligibility requirements, credit
                        limitations, carry forwards, carrybacks, transfer
                        elections, and reporting procedures, refer to the{' '}
                        <span style={{ fontWeight: 600 }}>
                          IRS{' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i3800.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            {' '}
                            Instructions{' '}
                          </Link>{' '}
                          for Form 3800, General Business Credit
                        </span>{' '}
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 87,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What types of credits are included on Form 3800?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                      Form 3800 includes various business credits such as the
                      Research and Development Credit, Work Opportunity Credit,
                      Energy-related credits, and much more.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0, mb: 1 }}>
                      These credits are offered to incentivize beneficial
                      economic activities and help reduce the total tax owed.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Do I need to file separate forms for each credit?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      No, Form 3800 consolidates multiple business credits,
                      allowing you to file them all together without needing
                      separate forms. However, for some credits, you may need to
                      complete additional documentation before filing Form 3800.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. How does Credit ordering work in Form 3800?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      General business credits reported on Form 3800 are treated
                      as used on a first-in, first-out basis by offsetting the
                      earliest-earned credits first.
                    </Typography>
                    <Typography component="div" sx={{ mt: 1 }}>
                      Therefore, the order in which the credits are used in any
                      tax year is:
                    </Typography>
                    <ol style={{ marginBottom: 16 }}>
                      <li style={{ marginBottom: 8 }}>
                        Carryforwards to that year, the earliest ones first;
                      </li>
                      <li style={{ marginBottom: 8 }}>
                        The general business credit earned in that year; and
                      </li>
                      <li style={{ marginBottom: 8 }}>
                        The carryback to that year.
                      </li>
                    </ol>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form3800;
